import { useEffect, useState } from "react";
import { api } from "../lib/api";

interface Item {
  id: string;
  name: string;
  quantity_value: number;
  quantity_unit: string;
  reason: string;
  supplier: string;
  checked: number;
}

export function ShoppingListModal({ onClose }: { onClose: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);

  async function generate() {
    setLoading(true);
    try {
      await api.post("/pantry/shopping-list");
      const { items } = await api.get<{ items: Item[] }>("/pantry/shopping-list/latest");
      setItems(items);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggle(id: string, checked: boolean) {
    setItems((rows) => rows.map((r) => (r.id === id ? { ...r, checked: checked ? 1 : 0 } : r)));
    await api.patch(`/pantry/shopping-list/items/${id}`, { checked });
  }

  function copyAsText() {
    const grouped = groupBySupplier(items);
    const text = Object.entries(grouped)
      .map(([supplier, rows]) => `${supplier}\n` + rows.map((r) => `- ${r.name}: ${r.quantity_value} ${r.quantity_unit} (${r.reason})`).join("\n"))
      .join("\n\n");
    navigator.clipboard?.writeText(text);
  }

  async function markAllBought() {
    await Promise.all(items.map((i) => toggle(i.id, true)));
  }

  const grouped = groupBySupplier(items);
  const remaining = items.filter((i) => !i.checked).length;

  return (
    <div className="dialog-backdrop modal-backdrop" onClick={onClose}>
      <div className="dialog modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="spread" style={{ alignItems: "flex-start" }}>
            <div>
              <span className="eyebrow">Shopping list · built from your shelf</span>
              <h2 style={{ margin: "10px 0 0", fontSize: 34, lineHeight: 1.05, letterSpacing: "-0.026em" }}>
                {items.length === 0 && !loading ? "You have everything" : `${remaining} things left to buy`}
              </h2>
            </div>
            <button className="btn btn-secondary" onClick={onClose} style={{ minWidth: 36, justifyContent: "center" }}>
              ✕
            </button>
          </div>
          <p className="muted" style={{ margin: "14px 0 0", maxWidth: "56ch", fontSize: 14, lineHeight: 1.6 }}>
            Amounts are what your saved recipes need minus what's already on the shelf.
          </p>
        </div>

        <div className="modal-body">
          {loading && <p className="muted">Working it out…</p>}
          {!loading && items.length === 0 && (
            <p className="muted" style={{ padding: "24px 0" }}>
              Nothing needed — either your shelf is stocked or you haven't saved any recipes yet.
            </p>
          )}
          {Object.entries(grouped).map(([supplier, rows]) => (
            <div key={supplier} style={{ padding: "20px 0 4px" }}>
              <div className="spread" style={{ alignItems: "baseline", borderBottom: "2px solid var(--color-divider)", paddingBottom: 8 }}>
                <h6 className="eyebrow" style={{ margin: 0 }}>
                  {supplier}
                </h6>
                <span className="muted-strong tabular" style={{ fontSize: 11.5 }}>
                  {rows.length} items
                </span>
              </div>
              {rows.map((item) => (
                <label
                  key={item.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "18px minmax(0,1fr) 90px",
                    gap: 14,
                    alignItems: "center",
                    padding: "12px 0",
                    borderBottom: "1px solid var(--color-divider)",
                    cursor: "pointer",
                    fontSize: 14,
                  }}
                >
                  <input type="checkbox" checked={!!item.checked} onChange={(e) => toggle(item.id, e.target.checked)} />
                  <span style={{ minWidth: 0, textDecoration: item.checked ? "line-through" : "none", opacity: item.checked ? 0.5 : 1 }}>
                    <span style={{ display: "block" }}>{item.name}</span>
                    <span className="muted-strong" style={{ display: "block", marginTop: 3, fontSize: 12 }}>
                      {item.reason}
                    </span>
                  </span>
                  <span className="tabular" style={{ fontFamily: "var(--font-heading)", fontWeight: 800, textAlign: "right" }}>
                    {item.quantity_value} {item.quantity_unit}
                  </span>
                </label>
              ))}
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <span className="muted-strong" style={{ fontSize: 12.5 }}>
            across {Object.keys(grouped).length} supplier{Object.keys(grouped).length === 1 ? "" : "s"}
          </span>
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button className="btn btn-secondary" onClick={copyAsText}>
              Copy as text
            </button>
            <button className="btn btn-primary" onClick={markAllBought}>
              Mark everything bought
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}

function groupBySupplier(items: Item[]): Record<string, Item[]> {
  const out: Record<string, Item[]> = {};
  for (const item of items) {
    (out[item.supplier] ??= []).push(item);
  }
  return out;
}
